import { FileText, Info, Maximize2, Minimize2, Sparkles } from 'lucide-react'
import { mutate } from '@/runtime'
import { QUICK_CHAT } from '@/lib/lead'
import type { PageResource } from '@/kinds/page/apply'
import { Icon } from '@/lib/icons'
import { Button, cx } from '@/components/ui'
import { TitleInput } from './TitleInput'

/** Cover, the quiet control row, the icon and the title. The editor follows it. */
export const PageHeader = ({
  page,
  cover,
  assistant,
  focusMode,
  onToggleFocus,
  onCover,
  onIcon,
  onAi,
  onDetails,
}: {
  page: PageResource
  cover: string | undefined
  assistant: boolean | null
  focusMode: boolean
  onToggleFocus: () => void
  onCover: () => void
  onIcon: () => void
  onAi: () => void
  /** the details, folded behind a button while the chat is docked beside the page */
  onDetails?: () => void
}) => (
  <>
    {cover && (
      <div
        className="group relative rounded-2xl overflow-hidden h-40 md:h-52"
        style={{ background: cover }}
        data-testid="page-cover"
      >
        <div className="absolute right-2 top-2 flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <Button
            variant="chip"
            className="bg-black/30 text-white"
            onClick={onCover}
            data-testid="page-cover-pick"
          >
            Change cover
          </Button>
          <Button
            variant="chip"
            className="bg-black/30 text-white"
            onClick={() => void mutate.patch(page.id, { spec: { cover: null } })}
          >
            Remove
          </Button>
        </div>
      </div>
    )}
    <div className={cx('px-1 md:px-6', cover ? 'pt-3' : 'pt-1')}>
      {/* quiet controls above the title, like Cortex's "Add cover" */}
      <div className="flex items-center gap-1 h-7 -ml-1 text-xs text-faint">
        {!cover && (
          <button
            type="button"
            onClick={onCover}
            className="px-1.5 h-6 rounded hover:bg-raised hover:text-muted"
            data-testid="page-cover-pick"
          >
            + Cover
          </button>
        )}
        {!page.spec.icon && (
          <button
            type="button"
            onClick={onIcon}
            className="px-1.5 h-6 rounded hover:bg-raised hover:text-muted"
          >
            + Icon
          </button>
        )}
        {/* the page's one thread, called Agent wherever it shows; on a phone the toolbar's Agent is it */}
        {assistant && (
          <button
            type="button"
            onClick={onAi}
            className="max-md:hidden px-1.5 h-6 rounded hover:bg-raised hover:text-muted inline-flex items-center gap-1"
            title="Ask this page’s thread to write, summarise or pull out tasks (or type /ai)"
            data-testid="page-ai"
          >
            <Sparkles size={12} aria-hidden /> {QUICK_CHAT}
          </button>
        )}
        <span className="flex-1" />
        {onDetails && (
          <button
            type="button"
            onClick={onDetails}
            className="px-1.5 h-6 rounded hover:bg-raised hover:text-muted inline-flex items-center gap-1"
            data-testid="page-details-open"
          >
            <Info size={12} aria-hidden /> Details
          </button>
        )}
        <button
          type="button"
          onClick={onToggleFocus}
          className="px-1.5 h-6 rounded hover:bg-raised hover:text-muted inline-flex items-center gap-1"
          title="Focus: hide both sidebars (⌘.)"
          data-testid="page-focus"
        >
          {focusMode ? <Minimize2 size={12} aria-hidden /> : <Maximize2 size={12} aria-hidden />}
          {focusMode ? 'Exit focus' : 'Focus'}
        </button>
      </div>
      <button
        type="button"
        onClick={onIcon}
        className={cx(
          'rounded-2xl leading-none flex items-center justify-center hover:bg-raised',
          page.spec.icon ? 'h-16 w-16 text-5xl' : 'h-10 w-10 text-faint',
        )}
        aria-label="Page icon"
        data-testid="page-icon"
      >
        {page.spec.icon ? <Icon value={page.spec.icon} size={44} /> : <FileText size={22} aria-hidden />}
      </button>
      <div className="mt-2 mb-2">
        <TitleInput key={`${page.id}:${page.spec.title}`} page={page} />
      </div>
    </div>
  </>
)
